import React, {useRef, useEffect} from 'react';
import {Link, useLocation} from 'react-router-dom';
import {useNavigate} from 'react-router-dom';
import classNames from 'classnames';
import Search from '../../assets/icons0/search-md.svg';
import Xcircle from '../../assets/icons0/x-circle.svg';
import PlusCircle from '../../assets/icons/plus-circle.svg';
import useQuery from '../../utilities/use-query';
import {useLazyLoadQuery} from 'react-relay/hooks';
import {graphqlOperatorInterfaceServiceByIdQuery} from './graphql';
import searchFilter from '../../utilities/search-filter';
import getImageUrl from './get-image-url';
import getTagValue from '../../utilities/get-tag-value';
import i18n from '../../utilities/i18n';
import {FormattedMessage} from 'react-intl';
import {ErrorBoundary} from 'react-error-boundary';
import Skeleton from '../skeleton/skeleton';
import fallbackComponent from '../fallback-component';
import SuspenseImageStaticSize from '../../utilities/suspense-image-static-size';
import Button from '../button/button';
import ArrowLeftIcon from '../../assets/icons0/arrow-left.svg';
import ArrowRightIcon from '../../assets/icons0/arrow-right.svg';
import {isInitialService} from './migration';
import {useSettingsState} from '../settings';

const debounce = (fn, delay = 1000) => {
  let timerId = null;
  return (...args) => {
    clearTimeout(timerId);
    timerId = setTimeout(() => fn(...args), delay);
  };
};

const EndpointRow = React.memo(({item}) => {
  const navigate = useNavigate();
  const img = getImageUrl(item.details, item.tags);
  const location = useLocation();
  const {locale = 'uk'} = useQuery();
  return (
    <div
      className='search-results-item display-flex justify-content-space-between border-bottom align-items-center'
      onClick={() => {
        navigate(`../step-5-2${searchFilter(location.search, {endpointId: item.id})}`);
      }}
    >
      <div className='cover width-2dot5rem'>
        <SuspenseImageStaticSize
          className='width-2dot5rem height-2dot5rem border-radius-0dot5rem'
          src={img}
        />
      </div>
      <div className='margin-left-0dot625rem flex-grow-1'>
        <div className='text-sm medium color-gray-900'>
          {i18n(item, 'name', locale)}
        </div>
      </div>
      <div className='display-flex align-items-center padding-0dot625rem'>
        <PlusCircle className='plus-circle-icon'/>
      </div>
    </div>
  )
});

const CatalogRow = React.memo(({item}) => {
  const navigate = useNavigate();
  const img = `https://${item.tags.coverUrl}`;
  const location = useLocation();
  const {locale = 'uk'} = useQuery();
  const params = {};
  if (item.endpoints[0]) {
    params.endpointId = item.endpoints[0].id
  } else {
    params.catalogId = item.id;
  }

  return (
    <div
      className='search-results-item display-flex justify-content-space-between border-bottom align-items-center'
      onClick={() => {
        navigate(`../step-5-2${searchFilter(location.search, params)}`);
      }}
    >
      <div className='cover width-2dot5rem'>
        <SuspenseImageStaticSize
          className='width-2dot5rem height-2dot5rem border-radius-0dot5rem'
          src={img}
        />
      </div>
      <div className='margin-left-0dot625rem flex-grow-1'>
        <div className='text-sm medium color-gray-900'>
          {i18n(item, 'name', locale)}
        </div>
      </div>
      <div className='display-flex align-items-center padding-0dot625rem'>
        <PlusCircle className='plus-circle-icon'/>
      </div>
    </div>
  )
});

const ListFooter = React.memo(({setPage, nextDisabled, prevDisabled, totalPages, currentPage}) => {
  return (
    <>
      <div className='list-footer__pagination padding-top-1dot5rem mw768-padding-top-1dot25rem padding-bottom-1dot5rem mw768-padding-bottom-1rem display-flex justify-content-space-between align-items-center'>
        <Button {...{type: 'button', color: 'secondary-gray', size: 'md', clickHandler: () => setPage(currentPage - 1), disabled: prevDisabled, additionalClasses: 'mx767-list-footer__square-btn'}}>
          <ArrowLeftIcon className='display-block width-1dot25rem height-1dot25rem'/>
        </Button>
        <div className='hide-large align-self-center color-gray-700 text-sm'>
          <span><FormattedMessage defaultMessage='Page'/></span>
          {' '}
          <span className='medium'>{currentPage + 1}</span>
          {' '}
          <span><FormattedMessage defaultMessage='of'/></span>
          {' '}
          <span className='medium'>{totalPages}</span>
        </div>
        <Button {...{type: 'button', color: 'secondary-gray', size: 'md', clickHandler: () => setPage(currentPage + 1), disabled: nextDisabled, additionalClasses: 'mx767-list-footer__square-btn'}}>
          <ArrowRightIcon className='display-block width-1dot25rem height-1dot25rem'/>
        </Button>
      </div>
    </>
  )
});

const Step51 = React.memo(({request, isPending}) => {
  const {defaultServiceId} = useSettingsState();
  const {serviceId = defaultServiceId, locale = 'uk'} = useQuery();
  // TODO: remove after successfull migration
  const hideCatalogs = isInitialService(serviceId);
  // TODO: End
  const {page, contain} = request;
  const {serviceById} = useLazyLoadQuery(graphqlOperatorInterfaceServiceByIdQuery, {id: serviceId});
  const endpoints = React.useMemo(
    () => serviceById.endpoints.slice().sort((a, b) => getTagValue(a.tags, 'rank', ':', Infinity) - getTagValue(b.tags, 'rank', ':', Infinity)), 
  [serviceById]);
  const navigate = useNavigate();
  const {catalogsByViewer} = useLazyLoadQuery(
    graphql`
        query step51CatalogsQuery($request: CatalogsRequestInput, $serviceId: String) {
            catalogsByViewer(request: $request) {
                totalPages,
                totalElements,
                content {
                    id
                    name
                    nameUk
                    price
                    tags
                    endpoints(serviceId: $serviceId) {
                      id
                      quantity
                      tags
                      end
                    }
                }
            }
        }
    `,
    {request, serviceId}
  );
  const [filter, setFilter] = React.useState(contain);
  const [searchFocused, setSearchFocused] = React.useState(false);
  const [searchTextAdded, setSearchTextAdded] = React.useState(false);
  const inputRef = useRef(null);
  const location = useLocation();
  useEffect(() => {
    inputRef.current.focus();
    window.scrollTo(0, 0);
  }, []);
  const setPage = React.useCallback((page) => {
    navigate(`${location.pathname}${searchFilter(location.search, {
      ...request,
      page
    })}`);
  }, []);
  const search = () => {
    const queryParams = {
      ...request,
      page: 0,
      contain: inputRef.current.value,
    };
    navigate(`${location.pathname}${searchFilter(location.search, queryParams)}`);
  };
  const startSearch = debounce(search, 500);
  const clearSearch = () => {
    setFilter('');
    setSearchTextAdded(false);
    navigate(`${location.pathname}${searchFilter(location.search, {
      ...request,
      contain: '',
      page: 0
    })}`);
  };
  const updateFilter = (event) => {
    setFilter(event.target.value);
    if (catalogsByViewer.totalElements > 0) {
      startSearch(event.target.value);
    }
    setSearchTextAdded(inputRef.current.value.length > 0);
  }
  return (<>
    <div className='mm-header background-color-gray-50 mm-header--search'>
      <div className='display-flex position-relative width-100percent align-items-center'>
        <div className={classNames('search-input-with-scan-button', {
          'active': searchFocused,
          'search-text-added': searchTextAdded,
        })}>
          <Search className='search-icon'/>
          <input id='filter'
            name='filter'
            type='text'
            value={filter}
            onChange={updateFilter}
            className='form-row__input'
            placeholder='Пошук товарів'
            ref={inputRef}
            onBlur={() => setSearchFocused(false)}
            onFocus={() => setSearchFocused(true)}
            autoComplete='off'
          />
          {searchTextAdded &&
            <Xcircle className='x-circle-icon' onClick={() => clearSearch()}/>
          }
        </div>
        <div>
          <Link to={`../step-5${searchFilter(location.search)}`} className='text-sm margin-left-1dot125rem color-primary-600'>
            <FormattedMessage defaultMessage='Cancel'/>
          </Link>
        </div>
      </div>  
    </div>
    <div className='fulfill-title text-lg medium border-bottom color-gray-900'>
      <FormattedMessage defaultMessage='Search results'/>
    </div>
    <div className={classNames('filtered-list', {'opacity-0dot4': isPending})}>
      {catalogsByViewer.totalElements > 0 && !hideCatalogs
        ?
        <>
          {catalogsByViewer.content.map(item =>
            <CatalogRow key={item.id} {...{item}}/>
          )}
          <div className='padding-left-1rem padding-right-1rem'>
            <ListFooter {...{setPage, totalPages: catalogsByViewer.totalPages, currentPage: parseInt(page), nextDisabled: parseInt(page) === catalogsByViewer.totalPages - 1, prevDisabled: parseInt(page) === 0}}/>
          </div>
        </>
        :
        endpoints.filter((item) => i18n(item, 'name', locale).toLowerCase().includes(filter.toLowerCase())).map(item =>
          <EndpointRow key={item.id} {...{item}}/>
        )
      }
    </div>
  </>);
});

const Step51Wrapper = () => {
  const {page = 0, contain = ''} = useQuery();
  const request = {
    size: 10,
    page: parseInt(page),
    contain,
    tagsStatus: 'active'
  };
  const deferredRequest = React.useDeferredValue(request);
  return (
    <ErrorBoundary {...{FallbackComponent: fallbackComponent}}>
      <React.Suspense fallback={<div className='padding-top-2rem padding-left-1rem padding-right-1rem mw768-padding-left-2rem mw768-padding-right-2rem'><Skeleton/></div>}>
        <Step51 {...{request: deferredRequest, isPending: request !== deferredRequest}}/>
      </React.Suspense>
    </ErrorBoundary>
  )
};

export default React.memo(Step51Wrapper);